import React, { useState, useEffect, useCallback } from 'react';
import Layout from './Layout';
import { useAuth } from '../context/AuthContext';
import { exportRowsToExcel } from '../utils/exportExcel';
import './Delivery.css';

const Delivery = () => {
  const { fetchWithAuth } = useAuth();
  const [deliveries, setDeliveries] = useState([]);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const loadDeliveries = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const response = await fetchWithAuth('/api/deliveries');
      if (!response.ok) throw new Error('Failed to load deliveries');
      const data = await response.json();
      setDeliveries(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load deliveries');
      setDeliveries([]);
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    loadDeliveries();
  }, [loadDeliveries]);

  const handleExport = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    const rows = deliveries.map((d) => ({
      'Transaction ID': d.transactionId || '',
      'Consigned location': d.consignedLocation || '',
      'Item count': Array.isArray(d.items) ? String(d.items.length) : '0',
      'Items JSON': JSON.stringify(d.items || []),
      'Signer': d.signerName || '',
      'Dispatched by': d.dispatchedBy || '',
      'Signed at': d.signedAt ? new Date(d.signedAt).toLocaleString() : '',
      Status: d.status || '',
    }));
    exportRowsToExcel(`dispatch-report-${stamp}`, 'Deliveries', rows);
  };

  const handleView = async (delivery) => {
    if (delivery.items && delivery.signerName !== undefined) {
      setSelectedDelivery(delivery);
      return;
    }
    try {
      const response = await fetchWithAuth(`/api/deliveries/${delivery.id}`);
      if (!response.ok) throw new Error('Failed to load delivery');
      const data = await response.json();
      setSelectedDelivery(data);
    } catch (err) {
      setError(err.message || 'Failed to load delivery details');
    }
  };

  return (
    <Layout>
      <div className="delivery-container">
        <div className="delivery-header">
          <h1>Deliveries</h1>
          <button type="button" className="delivery-refresh" onClick={loadDeliveries}>
            🔄 Refresh
          </button>
          <button type="button" className="delivery-refresh" onClick={handleExport} disabled={deliveries.length === 0}>
            ⬇ Excel
          </button>
        </div>
        <p className="delivery-subtitle">
          Dispatched orders with signed delivery notes from ChesiWMS-Lite.
        </p>
        {error && <div className="delivery-error">{error}</div>}
        {isLoading ? (
          <div className="delivery-loading">Loading...</div>
        ) : deliveries.length === 0 ? (
          <div className="delivery-empty">
            <div className="delivery-empty-icon">📋</div>
            <h2>No deliveries yet</h2>
            <p>Deliveries appear here after you complete a dispatch in <strong>ChesiWMS-Lite</strong> and sign the delivery note.</p>
            <ul className="delivery-empty-tips">
              <li>In the app, go to <strong>Admin</strong> and set <strong>ChesiWMS Lite API URL</strong> to this server (e.g. {typeof window !== 'undefined' && window.location?.origin ? window.location.origin : 'this server\'s URL'}).</li>
              <li>Log in on the app with the same account as this web app.</li>
              <li>Complete a dispatch (Dispatch → pick list → scan items → Complete), then enter the signer name and tap <strong>Submit</strong> when the delivery note popup appears.</li>
            </ul>
          </div>
        ) : (
          <div className="delivery-table-wrap">
            <table className="delivery-table">
              <thead>
                <tr>
                  <th>Transaction ID</th>
                  <th>Consigned Location</th>
                  <th>Items</th>
                  <th>Signed by</th>
                  <th>Dispatched by</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {deliveries.map((d) => (
                  <tr key={d.id}>
                    <td className="delivery-cell-id">{d.transactionId}</td>
                    <td>{d.consignedLocation}</td>
                    <td>{(d.items || []).length} item(s)</td>
                    <td>{d.signerName || '—'}</td>
                    <td>{d.dispatchedBy || '—'}</td>
                    <td className="delivery-cell-date">
                      {d.signedAt ? new Date(d.signedAt).toLocaleString() : (d.createdAt ? new Date(d.createdAt).toLocaleString() : '—')}
                    </td>
                    <td>
                      <span className={`delivery-status delivery-status-${(d.status || 'delivered').toLowerCase()}`}>
                        {d.status || 'Delivered'}
                      </span>
                    </td>
                    <td>
                      <button type="button" className="delivery-view-btn" onClick={() => handleView(d)}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedDelivery && (
        <div className="delivery-modal-overlay" onClick={() => setSelectedDelivery(null)}>
          <div className="delivery-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delivery-modal-header">
              <h2>Delivery Note</h2>
              <button type="button" className="delivery-modal-close" onClick={() => setSelectedDelivery(null)}>
                ✕
              </button>
            </div>
            <div className="delivery-modal-body">
              <div className="delivery-note-section">
                <div className="delivery-note-row">
                  <span className="delivery-note-label">Transaction ID</span>
                  <span className="delivery-note-value">{selectedDelivery.transactionId}</span>
                </div>
                <div className="delivery-note-row">
                  <span className="delivery-note-label">Consigned location</span>
                  <span className="delivery-note-value">{selectedDelivery.consignedLocation}</span>
                </div>
                <div className="delivery-note-row">
                  <span className="delivery-note-label">Status</span>
                  <span className={`delivery-status delivery-status-${(selectedDelivery.status || 'delivered').toLowerCase()}`}>
                    {selectedDelivery.status || 'Delivered'}
                  </span>
                </div>
              </div>
              <div className="delivery-note-section">
                <h3>Items dispatched</h3>
                <table className="delivery-note-items">
                  <thead>
                    <tr>
                      <th>Part number</th>
                      <th>Description</th>
                      <th>Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedDelivery.items || []).map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.partNumber}</td>
                        <td>{item.partDescription || '—'}</td>
                        <td>{item.qty}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="delivery-note-section delivery-note-signature">
                <h3>Signature</h3>
                <div className="delivery-signature-block">
                  <p className="delivery-signature-line">
                    <strong>Signed by:</strong> {selectedDelivery.signerName || '—'}
                  </p>
                  {selectedDelivery.dispatchedBy && (
                    <p className="delivery-signature-line">
                      <strong>Dispatched by:</strong> {selectedDelivery.dispatchedBy}
                    </p>
                  )}
                  <p className="delivery-signature-line">
                    <strong>Date:</strong>{' '}
                    {selectedDelivery.signedAt
                      ? new Date(selectedDelivery.signedAt).toLocaleString()
                      : selectedDelivery.createdAt
                        ? new Date(selectedDelivery.createdAt).toLocaleString()
                        : '—'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Delivery;
